import type { RefObject } from "react";
import Draggable from "react-draggable";
import type Konva from "konva";
import EditorComponent from "./EditorComponent";
import "@/styles/TextEditor.css";
import Box from "@mui/material/Box";
import type { PaperEditorMode } from "@store/slices/viewSlice";

interface Props {
  isEditorOpen: boolean; // Legacy prop for backward compatibility
  mode?: PaperEditorMode; // New mode prop: 'hidden' | 'modal' | 'fullscreen'
  stageRef?: RefObject<Konva.Stage>; // Canvas stage, for insert-diagram-as-image
}

/**
 * EditorModal - Wrapper for EditorComponent that handles different display modes
 * Modes:
 * - hidden: Component is not rendered
 * - modal: Draggable floating window (default)
 * - fullscreen: Full-screen editor without draggable wrapper
 */
export default function EditorModal({ isEditorOpen, mode = 'modal', stageRef }: Props) {
  // Use mode if provided, otherwise fall back to isEditorOpen for backward compatibility
  const effectiveMode = mode === 'hidden' || (!isEditorOpen && !mode) ? 'hidden' : mode;

  // Don't render if hidden
  if (effectiveMode === 'hidden') {
    return null;
  }

  // Fullscreen mode: render without draggable wrapper
  if (effectiveMode === 'fullscreen') {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1300,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <EditorComponent mode="fullscreen" stageRef={stageRef} />
      </Box>
    );
  }

  // Modal mode: render as draggable floating window
  return (
    <Draggable handle="#handle" cancel="#stop-drag">
      <Box
        sx={{
          position: "absolute",
          left: "35vw",
          top: "20vh",
          display: "block",
          zIndex: "999",
        }}
      >
        <EditorComponent mode="modal" stageRef={stageRef} />
      </Box>
    </Draggable>
  );
}
